import { useState, type MouseEvent } from 'react';
import {
  Alert, Box, Dialog, DialogContent, DialogTitle, Divider, IconButton, Stack, Tooltip, Typography,
} from '@mui/material';
import { Close, History } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { notesHistory, type NoteVersion } from '../services/notes-history.service';

const SOURCE: Record<string, string> = { ui: 'UI', mcp: 'MCP', api: 'API', system: 'system' };
const via = (v: NoteVersion) => (v.source ? ` · via ${SOURCE[v.source] ?? v.source}${v.client ? ` (${v.client})` : ''}` : '');

/** A small button that opens every version of an entity's note, with its date and author. Renders nothing without an id. */
export default function NotesHistoryButton({ entity, id, className }: { entity: string; id?: string | null; className?: string }) {
  const [open, setOpen] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['notes-history', entity, id],
    queryFn: () => notesHistory(entity, id!),
    enabled: open && !!id,
    staleTime: 0,
  });
  if (!id) return null;
  const show = (e: MouseEvent) => { e.stopPropagation(); setOpen(true); };
  return (
    <>
      <Tooltip title="Note history">
        <IconButton size="small" aria-label="Note history" onClick={show} onDoubleClick={(e) => e.stopPropagation()}
          className={className} sx={{ p: 0.25 }}>
          <History sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      {/* A portal still bubbles through React: a double-click on its text would start editing the grid cell behind it. */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth
        onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <DialogTitle sx={{ pr: 6 }}>
          Note history
          <IconButton aria-label="Close" onClick={() => setOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {isLoading && <Typography color="text.secondary">Loading…</Typography>}
          {error && <Alert severity="error">Could not load the note history.</Alert>}
          {data && !data.length && <Typography color="text.secondary">No note yet.</Typography>}
          <Stack divider={<Divider flexItem />} spacing={1.5}>
            {data?.map((v, i) => (
              <Box key={`${v.at}-${i}`} data-testid="note-version">
                <Typography variant="caption" color="text.secondary" component="div">
                  {v.at
                    ? <><Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{v.author ?? 'Unknown'}</Box> · {new Date(v.at).toLocaleString()}{via(v)}{i === 0 ? ' · current' : ''}</>
                    : v.kind === 'unrecorded' ? `${i === 0 ? 'Current · ' : ''}Changed without a record` : 'Before changes were recorded'}
                </Typography>
                {v.notes
                  ? <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.25 }}>{v.notes}</Typography>
                  : <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 0.25 }}>Cleared</Typography>}
              </Box>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
